import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import type { Coverage, Patient } from '@medplum/fhirtypes'
import { INSURANCE_RANKS } from '@/constants/insuranceConstants'
import type { CoverageTemplate, InsuranceCardSide, InsuranceRank } from '@/types/insuranceTypes'
import type { DraftCoverageMap } from './manageCoverages.types'

type UseDeferredCoverageDraftsOptions = {
  onCoverageSaved?: () => void
  ensurePatientResource: (patientId: string) => Promise<Patient>
  fetchCoverages: (patientId: string) => Promise<void>
  setPatientResource: (patient: Patient) => void
  upsertCoverageForPatient: (
    patientId: string,
    draft: CoverageTemplate,
    options?: { applyResolvedResult?: boolean },
  ) => Promise<Coverage>
  uploadInsuranceCardsForCoverage: (
    patientId: string,
    coverage: Coverage,
    rank: InsuranceRank,
    files: Partial<Record<InsuranceCardSide, File>>,
    validateCardSelection: (existingFront?: string, existingBack?: string) => boolean,
    suppressSuccessNotification?: boolean,
    applyResolvedResult?: boolean,
  ) => Promise<Coverage | null>
  base64ToFile: (base64: string, filename: string) => File
  validateCardSelection: (existingFront?: string, existingBack?: string) => boolean
}

export function useDeferredCoverageDrafts({
  onCoverageSaved,
  ensurePatientResource,
  fetchCoverages,
  setPatientResource,
  upsertCoverageForPatient,
  uploadInsuranceCardsForCoverage,
  base64ToFile,
  validateCardSelection,
}: UseDeferredCoverageDraftsOptions) {
  const [draftCoverages, setDraftCoverages] = useState<DraftCoverageMap>({})

  const setDraftCoverage = (draft: CoverageTemplate, previousRank?: InsuranceRank) => {
    setDraftCoverages((prev) => {
      const next = { ...prev }
      if (previousRank && previousRank !== draft.insuranceRank) {
        delete next[previousRank]
      }
      next[draft.insuranceRank] = draft
      return next
    })
  }

  const removeDraftCoverage = (rank: InsuranceRank) => {
    setDraftCoverages((prev) => {
      const next = { ...prev }
      delete next[rank]
      return next
    })
  }

  const persistDraftCoverages = async (createdPatientId: string) => {
    const orderedRanks = INSURANCE_RANKS.map((item) => item.value as InsuranceRank)

    for (const rank of orderedRanks) {
      const draft = draftCoverages[rank]
      if (!draft) {
        continue
      }

      let savedCoverage = await upsertCoverageForPatient(createdPatientId, draft, {
        applyResolvedResult: false,
      })

      if (draft.frontImageBase64 || draft.backImageBase64) {
        const uploadedCoverage = await uploadInsuranceCardsForCoverage(
          createdPatientId,
          savedCoverage,
          rank,
          {
            front: draft.frontImageBase64
              ? base64ToFile(draft.frontImageBase64, 'front-insurance-card.jpg')
              : undefined,
            back: draft.backImageBase64
              ? base64ToFile(draft.backImageBase64, 'back-insurance-card.jpg')
              : undefined,
          },
          validateCardSelection,
          true,
          false,
        )

        if (!uploadedCoverage) {
          throw new Error('Failed to upload insurance card images')
        }

        savedCoverage = uploadedCoverage
      }
    }

    await fetchCoverages(createdPatientId)
    setDraftCoverages({})
    setPatientResource(await ensurePatientResource(createdPatientId))
    notifications.show({
      title: 'Insurances created',
      message: 'Patient insurances have been created.',
      color: 'green',
    })
    onCoverageSaved?.()
  }

  return {
    draftCoverages,
    persistDraftCoverages,
    removeDraftCoverage,
    setDraftCoverage,
    setDraftCoverages,
  }
}
